import { AxiosError } from 'axios';
import type { TFunction } from 'i18next';
import { ERRORS } from '../../constants/error';

export type ErrorMessages = {
  validationError?: string;
  notFoundError?: string;
  conflictError?: string;
};

type ErrorResponse = {
  server?: string;
  email?: string;
  password?: string;
};

type ServerErrorResponse = {
  message: string | string[];
};

class ErrorHandler {
  private messages: ErrorMessages;
  private t: TFunction;

  constructor(t: TFunction, messages: ErrorMessages = {}) {
    this.t = t;
    this.messages = messages;
  }

  private handleNetworkError(): ErrorResponse {
    return { server: this.t('errors.serverError') };
  }

  private handleForbiddenError(
    error: AxiosError<ServerErrorResponse>,
  ): ErrorResponse {
    const message =
      typeof error.response?.data.message === 'string'
        ? error.response.data.message
        : this.t('errors.serverError');

    return {
      email: message,
      password: message,
    };
  }

  private handleBadRequestError(
    error: AxiosError<ServerErrorResponse>,
  ): ErrorResponse {
    const errorMessage = error.response?.data.message;
    
    if (typeof errorMessage === 'string') {
      return { server: errorMessage };
    }

    if (
      Array.isArray(errorMessage) &&
      errorMessage.length
    ) {
      const errMessage = errorMessage[0];
      return errMessage.toLowerCase().includes('email')
        ? { email: errMessage }
        : { password: errMessage };
    }
    return { server: this.t('errors.serverError') };
  }

  private handleValidationError(
    error: AxiosError<ServerErrorResponse>,
  ): ErrorResponse {
    const errorMessage = error.response?.data.message;

    // Use backend-translated message if available
    if (typeof errorMessage === 'string') {
      return { server: errorMessage };
    }

    if (Array.isArray(errorMessage) && errorMessage.length) {
      return { server: errorMessage[0] };
    }

    return { server: this.messages.validationError };
  }

  private handleNotFoundError(): ErrorResponse {
    return { server: this.messages.notFoundError };
  }

  private handleConflictError(): ErrorResponse {
    return { password: this.messages.conflictError };
  }

  private handleInternalServerError(): ErrorResponse {
    return { server: this.t('errors.serverError') };
  }

  private handleTooManyRequestsError(): ErrorResponse {
    return { server: this.t('errors.tooManyRequests') };
  }

  handleError(error: unknown): ErrorResponse {
    if (!(error instanceof AxiosError)) {
      return this.handleInternalServerError();
    }

    const errorHandlers = {
      [ERRORS.ERR_NETWORK]: () => this.handleNetworkError(),
      [ERRORS.FORBIDDEN]: () => this.handleForbiddenError(error),
      [ERRORS.NOT_FOUND]: () => this.handleNotFoundError(),
      [ERRORS.CONFLICT]: () => this.handleConflictError(),
      [ERRORS.BAD_REQUEST]: () => this.handleBadRequestError(error),
      [ERRORS.UNPROCESSABLE_ENTITY]: () => this.handleValidationError(error),
      [ERRORS.TOO_MANY_REQUESTS]: () => this.handleTooManyRequestsError(),
      [ERRORS.INTERNAL_SERVER_ERROR]: () => this.handleInternalServerError(),
    };

    const status = error.response?.status || error.code;
    const handler = errorHandlers[status as keyof typeof errorHandlers];
    return handler ? handler() : this.handleInternalServerError();
  }
}

/** Build an ErrorHandler with the given translator. Prefer this over `new`
 * so the i18n dependency is passed in (no hook calls inside the class). */
export const createErrorHandler = (t: TFunction, messages: ErrorMessages = {}) =>
  new ErrorHandler(t, messages);

export type { ErrorResponse };
export default ErrorHandler;
